import { useEffect, useState } from 'react'
import { Users, Globe, Mail, AlertTriangle, TrendingUp, Activity } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { api } from '@/lib/api'
import { toast } from 'sonner'

interface Stats {
  total_accounts: number
  active_accounts: number
  suspended_accounts: number
  total_domains: number
  total_mailboxes: number
  pending_abuse_flags: number
}

interface StatusService {
  slug: string
  name: string
  description: string | null
  status: string
  last_probed_at: string | null
}

const statusColor = (status: string) => {
  switch (status) {
    case 'operational':
      return 'text-emerald-500'
    case 'degraded':
      return 'text-amber-500'
    case 'partial_outage':
      return 'text-orange-500'
    case 'major_outage':
      return 'text-red-500'
    default:
      return 'text-muted-foreground'
  }
}

export function Overview() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [services, setServices] = useState<StatusService[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [statsRes, statusRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/status'),
        ])
        setStats(statsRes.data)
        setServices(statusRes.data.services || [])
      } catch {
        toast.error('Failed to load overview data')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const cards = [
    { title: 'Total Accounts', value: stats?.total_accounts ?? 0, icon: Users, color: 'text-blue-500' },
    { title: 'Active Accounts', value: stats?.active_accounts ?? 0, icon: Users, color: 'text-emerald-500' },
    { title: 'Total Domains', value: stats?.total_domains ?? 0, icon: Globe, color: 'text-violet-500' },
    { title: 'Total Mailboxes', value: stats?.total_mailboxes ?? 0, icon: Mail, color: 'text-pink-500' },
    { title: 'Suspended', value: stats?.suspended_accounts ?? 0, icon: AlertTriangle, color: 'text-red-500' },
    { title: 'Pending Abuse', value: stats?.pending_abuse_flags ?? 0, icon: AlertTriangle, color: 'text-amber-500' },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Overview</h1>
        <p className="text-sm text-muted-foreground mt-1">Platform health and key metrics</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <Card key={card.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">{card.title}</CardTitle>
              <card.icon className={`h-4 w-4 ${card.color}`} />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {loading ? <div className="h-8 w-16 bg-muted rounded animate-pulse" /> : card.value.toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Account Distribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Active</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-accent rounded-full overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${stats.total_accounts ? (stats.active_accounts / stats.total_accounts) * 100 : 0}%` }} />
                    </div>
                    <span className="font-medium w-10 text-right">{stats.active_accounts}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Suspended</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-accent rounded-full overflow-hidden">
                      <div className="h-full bg-red-500 rounded-full" style={{ width: `${stats.total_accounts ? (stats.suspended_accounts / stats.total_accounts) * 100 : 0}%` }} />
                    </div>
                    <span className="font-medium w-10 text-right">{stats.suspended_accounts}</span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Other</span>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-24 bg-accent rounded-full overflow-hidden">
                      <div className="h-full bg-muted-foreground rounded-full" style={{ width: `${stats.total_accounts ? ((stats.total_accounts - stats.active_accounts - stats.suspended_accounts) / stats.total_accounts) * 100 : 0}%` }} />
                    </div>
                    <span className="font-medium w-10 text-right">{stats.total_accounts - stats.active_accounts - stats.suspended_accounts}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-24 bg-muted rounded animate-pulse" />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4" />
              System Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-24 bg-muted rounded animate-pulse" />
            ) : services.length === 0 ? (
              <p className="text-sm text-muted-foreground">No status services configured.</p>
            ) : (
              <div className="space-y-2">
                {services.map((service) => (
                  <div key={service.slug} className="flex items-center justify-between text-sm">
                    <span>{service.name}</span>
                    <span className={`font-medium ${statusColor(service.status)}`}>
                      {service.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
